/**
 * UNOgames functions are used for reaching the endpoints for business.
 * @module UNOgames
 * @since 0.0.9
 */
import GlobalFunctions from '../global'

module.exports = {
	/**
	 * Call to unoapp API that retrieves list of campaigns.
	 * @function
	 * @param {integer} businessId - The id of the business.
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} pageNumber - The current page that we are retrieving results for.
	 * @param {integer} recordsPerPage - The number of records to return per page.
	 * @param {object} searchCriteria - Object with all criteria to be searched by.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getCampaigns (businessId, locationId, pageNumber, recordsPerPage, searchCriteria) {
		let httpParams = {business_id: businessId, location_id: locationId, page_number: pageNumber, records_per_page: recordsPerPage}

		if (searchCriteria) {
			for (let searchKey in searchCriteria) {
				httpParams[searchKey] = searchCriteria[searchKey]
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/gamification/campaign/list',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that retrieves list of Twilio accounts associated to the location.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getTwilioAccounts (locationId) {
		let httpParams = {location_id: locationId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/gamification/twilio/list',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a new campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {object} campaign - The details of the new campaign to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	createCampaign (locationId, campaign) {
		let httpParams = {location_id: locationId}

		if (campaign) {
			for (let key in campaign) {
				if (key !== 'details') {
					if (campaign[key] === false || campaign[key] === true) {
						httpParams[key] = GlobalFunctions.convertBooleanToInt(campaign[key])
					} else {
						httpParams[key] = campaign[key]
					}
				}
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/gamification/campaign/create',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that updates an existing campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign to be updated.
	 * @param {object} campaign - The details of the campaign to be updated.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	updateCampaign (locationId, campaignId, campaign) {
		let httpParams = {location_id: locationId}

		if (campaign) {
			for (let key in campaign) {
				if (key !== 'details') {
					if (campaign[key] === false || campaign[key] === true) {
						httpParams[key] = GlobalFunctions.convertBooleanToInt(campaign[key])
					} else {
						httpParams[key] = campaign[key]
					}
				}
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PATCH',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a campaign's details.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {object} campaignId - The id of the campaign details to be created.
	 * @param {object} campaignDetails - The details of the campaign to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	createCampaignDetails (locationId, campaignId, campaignDetails) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		if (campaignDetails) {
			for (let key in campaignDetails) {
				if (key === 'twilio_account') {
					httpParams[key] = campaignDetails[key].id
				} else if (key === 'keywords') {
					httpParams.keyphrase = campaignDetails[key]
				} else {
					httpParams[key] = campaignDetails[key]
				}
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/gamification/text2win/create',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that updates the contest page layout.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {object} form - The form specifications.
	 * @param {object} design - The design options for the layout.
	 * @param {object} agreement - The agreement specifications.
	 * @param {integer} contestId - The id of the contest being updated.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	updateContestLayout (locationId, campaignId, form, design, agreement, contestId) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		for (let key in form) {
			if (key !== 'email_fields') {
				if (form[key] === false || form[key] === true) {
					httpParams[key] = GlobalFunctions.convertBooleanToInt(form[key])
				} else {
					httpParams[key] = form[key]
				}
			} else {
				for (let emailKey in form.email_fields) {
					httpParams[`email_${emailKey}`] = form.email_fields[emailKey]
				}
			}
		}

		for (let key in design) {
			if (design[key] === false || design[key] === true) {
				httpParams[key] = GlobalFunctions.convertBooleanToInt(design[key])
			} else {
				httpParams[key] = design[key]
			}

			if (key === 'font_family') {
				httpParams.font_family = design.font_family.family
			}
		}

		for (let key in agreement) {
			if (key !== 'urls') {
				if (agreement[key] === false || agreement[key] === true) {
					httpParams[key] = GlobalFunctions.convertBooleanToInt(agreement[key])
				} else {
					httpParams[key] = agreement[key]
				}
			} else {
				agreement.urls.forEach((url, i) => {
					httpParams[`agreement_url_${i + 1}`] = url.url
					httpParams[`agreement_name_${i + 1}`] = url.name
				})
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PATCH',
				dataType: 'json',
				url: '/gamification/contest/' + contestId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates the contest page layout.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {object} form - The form specifications.
	 * @param {object} design - The design options for the layout.
	 * @param {object} agreement - The agreement specifications.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	createContestLayout (locationId, campaignId, form, design, agreement) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		for (let key in form) {
			if (key !== 'email_fields') {
				if (form[key] === false || form[key] === true) {
					httpParams[key] = GlobalFunctions.convertBooleanToInt(form[key])
				} else {
					httpParams[key] = form[key]
				}
			} else {
				for (let emailKey in form.email_fields) {
					httpParams[`email_${emailKey}`] = form.email_fields[emailKey]
				}
			}
		}

		for (let key in design) {
			if (design[key] === false || design[key] === true) {
				httpParams[key] = GlobalFunctions.convertBooleanToInt(design[key])
			} else {
				httpParams[key] = design[key]
			}

			if (key === 'font_family') {
				httpParams.font_family = design.font_family.family
			}
		}

		for (let key in agreement) {
			if (key !== 'urls') {
				if (agreement[key] === false || agreement[key] === true) {
					httpParams[key] = GlobalFunctions.convertBooleanToInt(agreement[key])
				} else {
					httpParams[key] = agreement[key]
				}
			} else {
				agreement.urls.forEach((url, i) => {
					httpParams[`agreement_url_${i + 1}`] = url.url
					httpParams[`agreement_name_${i + 1}`] = url.name
				})
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/gamification/contest/create',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that gets the details of a specific campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getCampaign (locationId, campaignId) {
		let httpParams = {location_id: locationId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that deletes a campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	deleteCampaign (locationId, campaignId) {
		let httpParams = {location_id: locationId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'DELETE',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns all of the details about the contest associated to the current campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getContestDetails (locationId, campaignId) {
		let httpParams = {location_id: locationId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId + '/get_contest',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns all of the prizes associated to the current campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getPrizes (locationId, campaignId) {
		let httpParams = {location_id: locationId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId + '/get_prize',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a new prize for the selected campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {object} prize - The details of the prize to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	createContestPrize (locationId, campaignId, prize) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		for (let key in prize) {
			if (prize[key] === false || prize[key] === true) {
				httpParams[key] = GlobalFunctions.convertBooleanToInt(prize[key])
			} else {
				httpParams[key] = prize[key]
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/gamification/prize',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that update an existing prize for the selected campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {integer} prizeId - The id of the prize being updated.
	 * @param {object} prize - The details of the prize to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	updateContestPrize (locationId, campaignId, prizeId, prize) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		for (let key in prize) {
			if (prize[key] === false || prize[key] === true) {
				httpParams[key] = GlobalFunctions.convertBooleanToInt(prize[key])
			} else {
				httpParams[key] = prize[key]
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PATCH',
				dataType: 'json',
				url: '/gamification/prize/' + prizeId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns all of the skill testing questions associated to the current campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getSkillTestingQuestions (locationId, campaignId) {
		let httpParams = {location_id: locationId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'GET',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId + '/skill_testing_question',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a new skill testing question for the selected campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {object} question - The details of the question to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	createSkillTestingQuestion (locationId, campaignId, question) {
		let httpParams = {location_id: locationId}

		for (let key in question) {
			if (question[key] === false || question[key] === true) {
				httpParams[key] = GlobalFunctions.convertBooleanToInt(question[key])
			} else {
				httpParams[key] = question[key]
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/gamification/campaign/' + campaignId + '/skill_testing_question',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that deletes the current skill testing question.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {integer} questionId - The id of the question to be deleted.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	deleteSkillTestingQuestion (locationId, campaignId, questionId) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'DELETE',
				dataType: 'json',
				url: '/gamification/skill_testing_question/' + questionId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a new prize rule for the selected prize.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {integer} prizeId - The id of the prize.
	 * @param {object} rule - The details of the rule to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	createContestPrizeRule (locationId, campaignId, prizeId, rule) {
		let httpParams = {location_id: locationId, campaign_id: campaignId}

		for (let key in rule) {
			if (key === 'date') {
				if (!rule.specific_day) {
					httpParams.date = 'null'
				} else {
					httpParams.date = rule.date
				}
			} else {
				if (rule[key] === false || rule[key] === true) {
					httpParams[key] = GlobalFunctions.convertBooleanToInt(rule[key])
				} else {
					httpParams[key] = rule[key]
				}
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/gamification/prize/' + prizeId + '/add_rule',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that updates the status of a campaign.
	 * @function
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} campaignId - The id of the campaign.
	 * @param {string} status - The status to be set.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	updateCampaignStatus (locationId, campaignId, status) {
		let httpParams = {location_id: locationId, status}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PATCH',
				dataType: 'json',
				url: '/gamification/campaign_status/' + campaignId,
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	}
}



// WEBPACK FOOTER //
// ./src/controllers/UNOgames.js